/**
 * Aspect — Furniture floor placement helpers (shared 2D/3D)
 * --------------------------------------------------------------
 * Footprint extents and wall-segment resolution used when dragging catalog
 * items in the 3D view and on the 2D plan editor.
 */

// Air gap kept between furniture and wall faces. Small on purpose: real
// furniture (toilets, dressers, beds) sits nearly flush against plaster.
const WALL_PADDING = 0.015;
const WALL_HALF_THICK = 0.075;
const DOOR_SLACK = 0.25;
/** Gap kept between solid furniture pieces (placement + walk colliders). */
export const OBSTACLE_PADDING = 0.06;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Half-extents along world X/Z after yaw (axis-aligned bounding box). */
export function extentsXZ(fp, rotDeg, scale = 1) {
  const r = (rotDeg * Math.PI) / 180;
  const c = Math.abs(Math.cos(r));
  const s = Math.abs(Math.sin(r));
  const hw = ((fp?.w ?? 1) / 2) * scale;
  const hd = ((fp?.d ?? 1) / 2) * scale;
  return { ex: c * hw + s * hd, ez: s * hw + c * hd };
}

/** True for wall-hung catalog items (paintings, TV, mirror cabinet, hood…). */
export function isWallMounted(metaOrRec) {
  return metaOrRec?.placement_mode === "wall";
}

/**
 * Air gap between a wall-hung item's back face and the wall surface.
 * Was 4mm — TVs/paintings sank into plaster and z-fought (moire on the wall).
 */
export const WALL_MOUNT_GAP = 0.022;

/** Extra clearance for thick/flat wall electronics that often z-fight. */
export function wallMountClearance(assetId) {
  const id = String(assetId ?? "");
  if (/^tv$/i.test(id) || /(^|_)tv($|_)/i.test(id) || /television|monitor|screen/i.test(id)) {
    return 0.038;
  }
  if (/painting|poster|mirror|frame/i.test(id)) return 0.02;
  return WALL_MOUNT_GAP;
}

/** Flat floor decor (rugs, mats) / stacked surface items should not block walking. */
export function blocksWalkCollision(p) {
  if (p?.parent_id) return false;
  if (p?.placement_mode === "surface") return false;
  // Wall art / ceiling fixtures hang above walking height.
  if (p?.placement_mode === "wall" || p?.placement_mode === "ceiling") return false;
  const h = p?.footprint?.h;
  if (h != null && h <= 0.12) return false;
  if (p?.category === "decor" && (h == null || h <= 0.2)) return false;
  if (typeof p?.asset_id === "string" && /carpet|rug|mat_/i.test(p.asset_id)) return false;
  if (typeof p?.asset_id === "string" && /^lamp_/i.test(p.asset_id)) return false;
  return true;
}

function closestOnSegment(ax, az, bx, bz, px, pz) {
  const abx = bx - ax;
  const abz = bz - az;
  const len2 = abx * abx + abz * abz;
  if (len2 < 1e-9) return { x: ax, z: az };
  const t = clamp(((px - ax) * abx + (pz - az) * abz) / len2, 0, 1);
  return { x: ax + t * abx, z: az + t * abz };
}

/**
 * Solid wall sub-segments with door openings cut out.
 * `doorSlack` widens the cut: the 0.25 m default keeps dragged furniture from
 * jamming door egress; wall-mount picking passes ~0 so paintings can hang
 * right up to a door frame instead of skipping to the wall behind it.
 */
export function buildWallSegments(room, { doorSlack = DOOR_SLACK } = {}) {
  if (!room?.walls) return [];
  const openings = room.openings ?? [];
  const segs = [];
  for (const w of room.walls) {
    const ax = w.start[0];
    const az = w.start[1];
    const bx = w.end[0];
    const bz = w.end[1];
    const len = Math.hypot(bx - ax, bz - az);
    if (len < 1e-4) continue;
    const half = (w.thickness ?? WALL_HALF_THICK * 2) / 2;

    const gaps = [];
    for (const op of openings) {
      if (op.wall !== w.id || op.type !== "door") continue;
      const tc = ((op.center[0] - ax) * (bx - ax) + (op.center[2] - az) * (bz - az)) / (len * len);
      const hw = ((op.width ?? 0.9) / 2 + doorSlack) / len;
      gaps.push([clamp(tc - hw, 0, 1), clamp(tc + hw, 0, 1)]);
    }
    gaps.sort((a, b) => a[0] - b[0]);

    const dirx = (bx - ax) / len;
    const dirz = (bz - az) / len;
    const add = (t0, t1) => {
      if (t1 - t0 < 0.02) return;
      segs.push({
        ax: ax + dirx * len * t0,
        az: az + dirz * len * t0,
        bx: ax + dirx * len * t1,
        bz: az + dirz * len * t1,
        half,
        nx: -dirz,
        nz: dirx,
      });
    };
    let cursor = 0;
    for (const [g0, g1] of gaps) {
      if (g0 > cursor) add(cursor, g0);
      cursor = Math.max(cursor, g1);
    }
    if (cursor < 1) add(cursor, 1);
  }
  return segs;
}

/**
 * Wall-mount pose from a cursor ray: intersect the ray with every wall's mount
 * plane (the wall face offset by the item's half thickness + a tiny air gap)
 * and return the nearest hit as a full pose — XZ flush on the wall, base Y from
 * the hit height, yaw facing away from the wall. Colliders match the mesh: no
 * padding is added, so the item sits exactly on the wall surface.
 *
 * @param {{x:number,y:number,z:number}} origin  ray origin (world)
 * @param {{x:number,y:number,z:number}} dir     normalized ray direction
 * @returns {{x:number,z:number,y:number,rotation_y:number}|null}
 */
export function wallMountPoseFromRay(origin, dir, room, footprint, scale = 1, assetId = "") {
  // Tiny door slack: art can hang beside a door frame; only the actual door
  // opening is unmountable (through it, the far wall legitimately wins).
  const segs = buildWallSegments(room, { doorSlack: 0.02 });
  if (!segs.length) return null;
  const fp = footprint ?? { w: 0.5, h: 0.5, d: 0.05 };
  const halfD = ((fp.d ?? 0.05) / 2) * scale;
  const halfW = ((fp.w ?? 0.5) / 2) * scale;
  const h = (fp.h ?? 0.5) * scale;
  const floorY = room?.floorY ?? 0;
  const wallH = room?.dimensions?.height ?? 2.6;
  const gap = wallMountClearance(assetId);

  // First SOLID wall face along the ray. Any mount farther than this would put
  // the art on a wall behind the one the player is actually looking at (it
  // renders as "buried inside the wall"). Door gaps aren't segments, so aiming
  // through an open doorway still legitimately reaches the far room's wall.
  let tSolid = Infinity;
  for (const s of segs) {
    const d0 = (origin.x - s.ax) * s.nx + (origin.z - s.az) * s.nz;
    const dn = dir.x * s.nx + dir.z * s.nz;
    if (Math.abs(dn) < 1e-6) continue;
    const side = d0 >= 0 ? 1 : -1;
    const tFace = (side * s.half - d0) / dn;
    if (tFace <= 0 || tFace >= tSolid) continue;
    const px = origin.x + dir.x * tFace;
    const py = origin.y + dir.y * tFace;
    const pz = origin.z + dir.z * tFace;
    if (py < floorY - 0.5 || py > floorY + wallH + 0.5) continue;
    const abx = s.bx - s.ax;
    const abz = s.bz - s.az;
    const len = Math.hypot(abx, abz);
    if (len < 1e-6) continue;
    const u = ((px - s.ax) * abx + (pz - s.az) * abz) / len;
    if (u < 0 || u > len) continue;
    tSolid = tFace;
  }

  let best = null;
  for (const s of segs) {
    // Signed distance of the ray origin from the wall centerline picks the face.
    const d0 = (origin.x - s.ax) * s.nx + (origin.z - s.az) * s.nz;
    const side = d0 >= 0 ? 1 : -1;
    const mount = side * (s.half + halfD + gap);
    const dn = dir.x * s.nx + dir.z * s.nz;
    if (Math.abs(dn) < 1e-6) continue;
    const t = (mount - d0) / dn;
    if (t <= 0 || (best && t >= best.t)) continue;
    // Never mount past the first solid wall the ray hits (small tolerance:
    // the mount plane sits a few cm proud of its own wall's face).
    if (t > tSolid + halfD + gap + 0.06) continue;

    const px = origin.x + dir.x * t;
    const py = origin.y + dir.y * t;
    const pz = origin.z + dir.z * t;
    // Reject hits far outside the wall's vertical span (looking at sky/ground).
    if (py < floorY - 0.5 || py > floorY + wallH + 0.5) continue;

    // Clamp along the wall so the item's width stays on this segment (no
    // overhang past the ends — that reaches into corners / adjacent walls).
    const abx = s.bx - s.ax;
    const abz = s.bz - s.az;
    const len = Math.hypot(abx, abz);
    if (len < 1e-6) continue;
    const ux = abx / len;
    const uz = abz / len;
    let u = (px - s.ax) * ux + (pz - s.az) * uz;
    if (u < -0.02 || u > len + 0.02) continue;
    u = len < 2 * halfW ? len / 2 : clamp(u, halfW, len - halfW);

    const nx = s.nx * side;
    const nz = s.nz * side;
    const baseY = clamp(py - h / 2, floorY + 0.05, floorY + wallH - h - 0.02);
    best = {
      t,
      x: s.ax + ux * u + nx * (s.half + halfD + gap),
      z: s.az + uz * u + nz * (s.half + halfD + gap),
      y: baseY,
      rotation_y: (Math.atan2(nx, nz) * 180) / Math.PI,
    };
  }
  if (!best) return null;
  return { x: best.x, z: best.z, y: best.y, rotation_y: best.rotation_y };
}

/**
 * Push a footprint center out of every wall. `ref` (usually the piece's
 * CURRENT position) pins which side of each wall the piece belongs to — while
 * dragging, the raw cursor point often lands on/behind the wall centerline and
 * a naive nearest-side push flips the piece between the two faces every frame
 * (violent jitter against walls). With a ref the piece just slides along its
 * own face, however deep the cursor goes.
 */
function resolveAgainstWalls(wx, wz, footprint, rotDeg, scale, segs, ref = null) {
  if (!segs.length) return { x: wx, z: wz };
  const { ex, ez } = extentsXZ(footprint, rotDeg, scale);
  let x = wx;
  let z = wz;

  for (let pass = 0; pass < 4; pass++) {
    let moved = false;
    for (const s of segs) {
      const cp = closestOnSegment(s.ax, s.az, s.bx, s.bz, x, z);
      let dx = x - cp.x;
      let dz = z - cp.z;
      const dist = Math.hypot(dx, dz);

      // Out-direction: from the side the piece is ALREADY on (ref), falling
      // back to the target point's side, then the segment normal.
      let nx;
      let nz;
      const rdx = (ref?.x ?? x) - cp.x;
      const rdz = (ref?.z ?? z) - cp.z;
      const rdist = Math.hypot(rdx, rdz);
      if (rdist > 1e-6) {
        nx = rdx / rdist;
        nz = rdz / rdist;
      } else if (dist > 1e-6) {
        nx = dx / dist;
        nz = dz / dist;
      } else {
        nx = s.nx;
        nz = s.nz;
      }

      const reach = s.half + (ex * Math.abs(nx) + ez * Math.abs(nz)) + WALL_PADDING;
      // Signed offset along the chosen out-direction (negative = cursor pushed
      // past the centerline toward the far side).
      const along = dx * nx + dz * nz;
      if (along < reach - 1e-4) {
        x = cp.x + nx * reach;
        z = cp.z + nz * reach;
        moved = true;
      }
    }
    if (!moved) break;
  }
  return { x, z };
}

/** Axis-aligned obstacle boxes for other placements (world XZ). */
export function buildPlacementObstacles(placements, excludeId = null, from = null) {
  const exclude =
    excludeId instanceof Set ? excludeId : excludeId ? new Set([excludeId]) : null;
  const out = [];
  for (const p of placements ?? []) {
    if (!p?.id || exclude?.has(p.id)) continue;
    if (p.parent_id && exclude?.has(p.parent_id)) continue;
    if (!blocksWalkCollision(p)) continue;
    const pos = p.position;
    if (!pos) continue;
    const scale = p.scale ?? 1;
    const rotY = p.rotation_y ?? 0;
    const { ex, ez } = extentsXZ(p.footprint ?? { w: 1, d: 1 }, rotY, scale);
    const box = {
      cx: pos[0],
      cz: pos[2],
      ex: ex + OBSTACLE_PADDING,
      ez: ez + OBSTACLE_PADDING,
    };
    // Already overlapping this neighbor (cushions, a tight start pose) — don't
    // glue the drag. Only NEW collisions should push.
    if (
      from &&
      Number.isFinite(from.x) &&
      aabbOverlaps(from.x, from.z, from.ex ?? 0, from.ez ?? 0, box)
    ) {
      continue;
    }
    out.push(box);
  }
  return out;
}

function aabbOverlaps(x, z, ex, ez, o) {
  return Math.abs(x - o.cx) < ex + o.ex - 1e-4 && Math.abs(z - o.cz) < ez + o.ez - 1e-4;
}

/** True when a footprint center would intersect another solid placement. */
export function placementOverlaps(wx, wz, { footprint, rotation_y = 0, scale = 1, excludeId = null }, placements) {
  const { ex, ez } = extentsXZ(footprint ?? { w: 1, d: 1 }, rotation_y, scale);
  const pad = OBSTACLE_PADDING;
  for (const o of buildPlacementObstacles(placements, excludeId)) {
    if (aabbOverlaps(wx, wz, ex + pad, ez + pad, o)) return true;
  }
  return false;
}

function resolveAgainstObstacles(x, z, ex, ez, obstacles) {
  if (!obstacles.length) return { x, z };
  const pad = OBSTACLE_PADDING;
  const hx = ex + pad;
  const hz = ez + pad;
  for (let pass = 0; pass < Math.max(4, obstacles.length); pass++) {
    let moved = false;
    for (const o of obstacles) {
      const dx = x - o.cx;
      const dz = z - o.cz;
      const overlapX = hx + o.ex - Math.abs(dx);
      const overlapZ = hz + o.ez - Math.abs(dz);
      if (overlapX <= 0 || overlapZ <= 0) continue;
      if (overlapX < overlapZ) x += (dx >= 0 ? 1 : -1) * overlapX;
      else z += (dz >= 0 ? 1 : -1) * overlapZ;
      moved = true;
    }
    if (!moved) break;
  }
  return { x, z };
}

/**
 * Clamp a furniture center in world XZ so its footprint stays inside the room,
 * clear of walls, and does not overlap other solid placements.
 */
export function resolveFurnitureXZ(
  wx,
  wz,
  { footprint, rotation_y = 0, scale = 1, excludeId = null, prev = null },
  room,
  placements = null,
) {
  const fp = footprint ?? { w: 1, d: 1 };
  const bounds = room?.bounds;
  const segs = buildWallSegments(room);
  const { ex, ez } = extentsXZ(fp, rotation_y, scale);
  const from =
    prev && Number.isFinite(prev.x) && Number.isFinite(prev.z)
      ? { x: prev.x, z: prev.z, ex, ez }
      : null;
  const obstacles = placements ? buildPlacementObstacles(placements, excludeId, from) : [];

  let x = wx;
  let z = wz;
  if (bounds) {
    x = clamp(x, bounds.min[0] + ex, bounds.max[0] - ex);
    z = clamp(z, bounds.min[2] + ez, bounds.max[2] - ez);
  }

  // Two passes only: stay out of walls, then out of NEW furniture overlaps.
  // Never freeze on the previous pose — that made couches feel glued in place.
  const w = resolveAgainstWalls(x, z, fp, rotation_y, scale, segs, prev);
  x = w.x;
  z = w.z;
  if (obstacles.length) {
    const o = resolveAgainstObstacles(x, z, ex, ez, obstacles);
    const w2 = resolveAgainstWalls(o.x, o.z, fp, rotation_y, scale, segs, prev);
    x = w2.x;
    z = w2.z;
  }

  if (bounds) {
    x = clamp(x, bounds.min[0] + ex, bounds.max[0] - ex);
    z = clamp(z, bounds.min[2] + ez, bounds.max[2] - ez);
  }
  return { x, z };
}
